/**
 * Connected AI Case Summary card. Runs the cheap ``/summary/status``
 * pre-flight, lazily fetches the cached payload, owns the generate /
 * regenerate / download-PDF mutations, and resolves all of it into the
 * pure {@link CaseSummaryCardView}'s prop-driven render state.
 */

import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  CaseSummaryCardView,
  type CaseSummaryCardViewProps,
} from '@/components/case-overview/CaseSummaryCardView'
import {
  useCaseSummary,
  useCaseSummaryStatus,
  useGenerateCaseSummary,
} from '@/hooks/use-case-queries'
import { pdfApi } from '@/services/api/pdf'
import { resolveLanguage } from '@/services/i18n/languages'

/**
 * ``showInvestigationList`` — when false, suppresses the per-investigation
 * mini-rows in the summary state. The dedicated Case Summary page renders
 * richer ``InvestigationDigestCard``s for the same data, so it opts out to
 * avoid duplicating the list. Defaults to true to preserve standalone
 * behaviour.
 */
export function CaseSummaryCard({
  caseId,
  showInvestigationList = true,
}: {
  caseId: string
  showInvestigationList?: boolean
}) {
  const { i18n, t } = useTranslation()
  const statusQ = useCaseSummaryStatus(caseId)
  const summaryQ = useCaseSummary(caseId, Boolean(statusQ.data?.has_summary))
  const generateM = useGenerateCaseSummary(caseId)

  // Download PDF — uses the existing case_summary template the backend
  // already filters by ``includedInReport=True``. Kept inline (not the
  // shared hook) so we can surface the loading + error toast right here.
  const downloadM = useMutation({
    mutationFn: async () => {
      const blob = await pdfApi.generate({
        template: 'case_summary',
        subjectRef: caseId,
        caseId,
        locale: resolveLanguage(i18n.language),
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `case_summary_${caseId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    onError: (err: Error) =>
      toast.error(err.message || t('cases.summaryCard.downloadError', 'Failed to download report')),
  })

  const status = statusQ.data
  const eligibleCount = status?.eligible_investigation_count ?? 0
  // A summary builds from the case's hypotheses as well (PRD-0075 R-E5).
  const hypothesisCount = status?.hypothesis_count ?? 0
  const hasSummary = Boolean(status?.has_summary)

  let state: CaseSummaryCardViewProps['state']
  if (statusQ.isLoading) state = 'loading'
  else if (eligibleCount === 0 && hypothesisCount === 0) state = 'empty'
  else if (!hasSummary) state = 'generate'
  else if (summaryQ.isLoading || !summaryQ.data) state = 'loading'
  else state = 'summary'

  return (
    <CaseSummaryCardView
      state={state}
      eligibleCount={eligibleCount}
      envelope={summaryQ.data ?? null}
      investigationsLink={`/cases/${caseId}/investigations`}
      showInvestigationList={showInvestigationList}
      isGenerating={generateM.isPending}
      isDownloading={downloadM.isPending}
      onGenerate={() => generateM.mutate('en')}
      onDownload={() => downloadM.mutate()}
    />
  )
}
